import * as Sentry from "@sentry/react"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import type { RateMini } from "ipctg-inflow-types/InFlowAPIMisc"
import {
  ContactType,
  DiscountDisplayMode,
} from "ipctg-inflow-types/InFlowEnums"
import { useState } from "react"
import {
  GridList,
  GridListItem,
  GridListItemProps,
} from "react-aria-components"
import { useTranslation } from "react-i18next"

import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import { cn, formatDateTimeWithTimeZone } from "@/utilities/etc"
import {
  parkCheckoutQueryOptions,
  parkRatesQueryOptions,
} from "@/utilities/hooks/queries/park"
import { prettyCurrencyNumber } from "@/utilities/normalizers"

import { RatePassphraseModal } from "./-components/RatePassphraseModal"
import { RateWarningModal } from "./-components/RateWarningModal"
import styles from "./rates.module.css"

interface RatesSearchParams {
  phone: string
  email: string
  contactType: ContactType
  sessionEndDateTime: string | undefined
  canExtend: boolean | undefined
}

export const Route = createFileRoute("/qr/$qrLookupID/park/$epoch/rates")({
  staticData: {
    pageName: "Select Rate",
  },
  validateSearch: (search: Record<string, unknown>): RatesSearchParams => ({
    phone: (search.phone as string) || "",
    email: (search.email as string) || "",
    contactType:
      (search.contactType as string) === ContactType.Email
        ? ContactType.Email
        : ContactType.Phone,
    sessionEndDateTime: (search.sessionEndDateTime as string) || undefined,
    canExtend: (search.canExtend as string) === "true" || undefined,
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation("park")
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const params = Route.useParams()
  const { sessionState } = Route.useRouteContext()
  const { canExtend, sessionEndDateTime, phone, email, contactType } =
    Route.useSearch()

  // Passphrase modal state
  const [passphraseRateState, setPassphraseRateState] = useState<{
    rateID: string
    label: string
    warningMessage: string | null
  } | null>(null)

  // Warning modal state
  const [warningRateState, setWarningRateState] = useState<{
    rateID: string
    warningMessage: string
  } | null>(null)

  // Fetch rates from API
  const { data } = useSuspenseQuery(parkRatesQueryOptions())

  // Show message if no rates available
  if (data.rates.length === 0) {
    return (
      <Card>
        <Heading level={1} as={2} className="center">
          {t("rates.noRatesTitle")}
        </Heading>
        <Text className="center">{data.noRatesMessage}</Text>
      </Card>
    )
  }

  return (
    <>
      {canExtend && sessionState.plate && (
        <Card>
          <Heading level={3}>{t("rates.activeSession")}</Heading>
          <div
            style={{
              display: "flex",
              gap: "var(--space-2)",
            }}
          >
            <Icon name="car" />
            <Text level={2} style={{ fontWeight: 600 }}>
              {sessionState.plate}
            </Text>
          </div>
          {sessionEndDateTime && (
            <Text level={3}>
              {t("rates.sessionExpires", {
                time: formatDateTimeWithTimeZone(sessionEndDateTime),
              })}
            </Text>
          )}
        </Card>
      )}

      <Card>
        <Heading level={1}>{t("rates.heading")}</Heading>
        <GridList
          aria-label={t("rates.heading")}
          items={data.rates}
          className={styles.ratesContainer}
          selectionMode="single"
          onAction={(key) => {
            const rate = data.rates.find((r) => r.rateID === key)
            if (rate) {
              handleRateClick({ ...rate })
            } else {
              console.error("No rate found for: ", key)
              Sentry.captureException(new Error(`Rate not found: ${key}`), {
                extra: {
                  rateID: key,
                  availableRates: data.rates.map((r) => r.rateID),
                  qrLookupID: params.qrLookupID,
                },
                tags: {
                  section: "park",
                  flow: "rate-selection",
                },
              })
            }
          }}
        >
          {(rate) => (
            <RateCard rate={rate} id={rate.rateID} textValue={rate.label} />
          )}
        </GridList>
      </Card>

      {/* Passphrase Modal */}
      {passphraseRateState && (
        <RatePassphraseModal
          isOpen={true}
          onOpenChange={(isOpen: boolean) => {
            if (!isOpen) setPassphraseRateState(null)
          }}
          rateID={passphraseRateState.rateID}
          rateLabel={passphraseRateState.label}
          onSuccess={handlePassphraseSuccess}
        />
      )}

      {/* Warning Modal */}
      {warningRateState && (
        <RateWarningModal
          isOpen={true}
          onOpenChange={(isOpen: boolean) => {
            if (!isOpen) setWarningRateState(null)
          }}
          warningMessage={warningRateState.warningMessage}
          onProceed={() => handleWarningProceed(warningRateState.rateID)}
        />
      )}
    </>
  )

  function handleRateClick(args: {
    rateID: string
    label: string
    requiresPassphrase: boolean
    warningMessage: string | null
  }) {
    // If passphrase protected, open passphrase modal first
    // After passphrase success, warning modal will be shown if needed
    if (args.requiresPassphrase) {
      setPassphraseRateState({ ...args })
      return
    }

    // If has warning message, show warning modal
    if (args.warningMessage) {
      setWarningRateState({
        rateID: args.rateID,
        warningMessage: args.warningMessage,
      })
      return
    }

    navigateToCart(args.rateID)
  }

  function handlePassphraseSuccess(rateID: string) {
    setPassphraseRateState(null)

    // Check if there's a warning message for this rate
    const rate = data.rates.find((r) => r.rateID === rateID)
    if (rate?.warningMessage) {
      setWarningRateState({ rateID, warningMessage: rate.warningMessage })
    } else {
      navigateToCart(rateID)
    }
  }

  function handleWarningProceed(rateID: string) {
    navigateToCart(rateID)
  }

  function navigateToCart(rateID: string) {
    queryClient.removeQueries(
      parkCheckoutQueryOptions({ rateID, epoch: params.epoch })
    )
    navigate({
      to: "/qr/$qrLookupID/park/$epoch/cart",
      search: { rateID, phone, email, contactType },
    })
  }
}

interface RateCardProps {
  rate: RateMini
}

function RateCard(props: RateCardProps & GridListItemProps) {
  const { rate, ...rest } = props

  return (
    <GridListItem {...rest} className={cn(rest.className, styles.rateCard)}>
      <div className={styles.rateInfo}>
        <Heading level={5} as={2}>
          {rate.label}
        </Heading>
        <RateTags rate={rate} />
        <RateNextStepDisclaimer rate={rate} />
      </div>

      <RatePricing rate={rate} />
      <Icon name="chevronRight" />
    </GridListItem>
  )
}

function RateTags(props: { rate: RateMini }) {
  const { t } = useTranslation("park")

  if (props.rate.requiresPassphrase) {
    return (
      <div
        className={styles.rateTag}
        style={{ marginTop: "var(--space-2)" }}
        data-type="passphrase"
      >
        <Text level="disclaimer" className="medium" as="span">
          <Icon name="textEntry" />
          {t("rates.passphraseProtected")}
        </Text>
      </div>
    )
  }

  const promotions = props.rate.autoApplyPromotions.filter(
    (promotion) => promotion.userIsEligible
  )

  if (!promotions.length) return <></>

  // TODO, will need to adjust css once we have support stackable promotions
  return (
    <div
      className={styles.autoApplyTags}
      style={{ marginTop: "var(--space-2)" }}
    >
      {promotions.map((promotion, index) => (
        <div className={styles.rateTag} data-type="promotion" key={index}>
          <Text level="disclaimer" className="medium" as="span">
            <Icon name="promotion" />
            {promotion.userFacingRedeemCopy}
          </Text>
        </div>
      ))}
    </div>
  )
}

function RateNextStepDisclaimer(props: { rate: RateMini }) {
  const { t } = useTranslation("park")
  const { rate } = props
  if (
    rate.autoApplyPromotions.filter((promotion) => promotion.userIsEligible)
      .length &&
    (rate.discountDisplayMode === DiscountDisplayMode.nextStep ||
      rate.discountedAmountDue === null)
  ) {
    return (
      <Text level="disclaimer" className={styles.nextStepPromotionDisclaimer}>
        {t("rates.seeFinalPrice")}
      </Text>
    )
  }

  return <></>
}

function RatePricing(props: { rate: RateMini }) {
  const { t } = useTranslation("park")
  const { rate } = props
  if (rate.requiresPassphrase) {
    return (
      <Text level={2} className="semi-bold">
        --
      </Text>
    )
  }
  if (
    !rate.autoApplyPromotions.filter((promotion) => promotion.userIsEligible)
      .length
  ) {
    if (!props.rate.rateAmount) {
      return (
        <Text level={2} className={cn("semi-bold", styles.promotional)}>
          {t("rates.free")}
        </Text>
      )
    }

    return (
      <Text level={2} className="semi-bold">
        {prettyCurrencyNumber(props.rate.rateAmount)}
      </Text>
    )
  }

  // Show both the original price with a strike through and the new discounted amount
  if (
    rate.discountDisplayMode === DiscountDisplayMode.strikethroughPricing &&
    rate.discountedAmountDue !== null
  ) {
    return (
      <div className={styles.strikeThroughPromotion}>
        <Text level={2} style={{ textDecoration: "line-through" }}>
          {prettyCurrencyNumber(props.rate.rateAmount)}
        </Text>
        <Text level={2} className={cn("semi-bold", styles.promotional)}>
          {rate.discountedAmountDue === 0
            ? t("rates.free")
            : prettyCurrencyNumber(rate.discountedAmountDue)}
        </Text>
      </div>
    )
  }

  // User must go to the next page to see the final price
  return (
    <Text level={2} className={cn("semi-bold", styles.promotional)}>
      <span style={{ textDecoration: "line-through" }}>
        {prettyCurrencyNumber(props.rate.rateAmount)}
      </span>
      *
    </Text>
  )
}
